<script setup>
import notification from "@/utils/plugins/NotificationService";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useAuthStore } from "@/app/stores/authStore";
import { useUiStore } from "@/app/stores/uiStore";

import HeaderTitle from "@/components/layout/HeaderTitle";
import SharedDeleteConfirmDialog from "@/components/shared/SharedDeleteConfirmDialog.vue";
import IconMeetingRoomReservation from "@/components/icons/IconMeetingRoomReservation.vue";
import MeetingRoomReservationDialog from "@/components/shared/Communication/MeetingRoomReservationDialog.vue";
import EmployeeService from "@/api/services/employee.service";
import MeetingRoomService from "@/api/services/meetingRoom.service";
import PublicHolidayService from "@/api/services/publicHoliday.service";
import { filterByNormalizedText, normalizeSearchText } from "@/utils/textSearch";


const authStore = useAuthStore();
const uiStore = useUiStore();

const selectedDate = ref(getTodayDate());
const selectedFromDate = ref(getTodayDate());
const selectedToDate = ref(getTodayDate());
const calendarView = ref("week");
const selectedEmployee = ref(null);
const employees = ref([]);
const employeesOriginal = ref([]);
const meetingRooms = ref([]);
const publicHolidays = ref([]);
const dialogNewReservation = ref(false);
const dialogDeleteReservation = ref(false);
const isDeletingReservation = ref(false);
const selectedReservation = ref(null);
const selectedReservationStartAt = ref(null);
const selectedReservationReadonly = ref(false);
const currentDateTime = ref(new Date());
let currentTimeIntervalId = null;

const params = ref({});

const viewModes = [
    { value: "day", label: "Día" },
    { value: "week", label: "Semana" },
    { value: "month", label: "Mes" },
];

const timeSlots = buildTimeSlots();
const mobileSlotHeight = 52;
const timelineStartMinutes = 8 * 60;
const timelineEndMinutes = (20 * 60) + 30;

const user = computed(() => {
    return authStore.getUserLogged;
});

const isAdmin = computed(() => {
    return user.value?.roles?.includes("ROLE_ADMIN");
});

const userWorkplace = computed(() => {
    return user.value?.workplace || null;
});

const userWorkplaceId = computed(() => {
    return userWorkplace.value?.id || null;
});

const rangeStartDate = computed(() => {
    return parseDate(selectedFromDate.value);
});

const rangeEndDate = computed(() => {
    return parseDate(selectedToDate.value);
});

const currentPeriodTitle = computed(() => {
    if (calendarView.value === "day") {
        return formatLongDate(rangeStartDate.value);
    }

    if (calendarView.value === "week") {
        return `${formatDateOnly(rangeStartDate.value)} - ${formatDateOnly(rangeEndDate.value)}`;
    }

    if (isSameMonth(rangeStartDate.value, rangeEndDate.value)) {
        return capitalize(formatMonthYear(rangeStartDate.value));
    }

    return `${capitalize(formatMonthYear(rangeStartDate.value))} - ${capitalize(formatMonthYear(rangeEndDate.value))}`;
});

const selectedPublicHolidayLabel = computed(() => {
    if (calendarView.value !== "day") {
        return "";
    }

    return getPublicHolidayLabel(selectedDate.value);
});

const dayColumns = computed(() => {
    if (calendarView.value === "day") {
        return [buildCalendarDay(rangeStartDate.value)];
    }

    return getDatesBetween(rangeStartDate.value, rangeEndDate.value).map(buildCalendarDay);
});

const monthCalendars = computed(() => {
    return getMonthsBetween(rangeStartDate.value, rangeEndDate.value).map((monthDate) => {
        return {
            key: formatMonthKey(monthDate),
            title: capitalize(formatMonthYear(monthDate)),
            days: buildMonthDays(monthDate),
        };
    });
});

const buildMonthDays = (monthDate) => {
    const firstDayOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const lastDayOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    const firstCalendarDay = getStartOfWeek(firstDayOfMonth);
    const lastCalendarDay = getEndOfWeek(lastDayOfMonth);
    const visibleDays = getDaysDifference(firstCalendarDay, lastCalendarDay) + 1;
    const days = [];

    for (let index = 0; index < visibleDays; index += 1) {
        const day = addDays(firstCalendarDay, index);
        days.push({
            ...buildCalendarDay(day),
            currentMonth: day.getMonth() === firstDayOfMonth.getMonth(),
            inSelectedRange: isDateInRange(day, rangeStartDate.value, rangeEndDate.value),
        });
    }

    return days;
};

const reservationsByDay = computed(() => {
    return meetingRooms.value.reduce((days, reservation) => {
        const dateKey = formatDatePart(new Date(reservation.startAt));
        if (!days[dateKey]) {
            days[dateKey] = [];
        }
        days[dateKey].push(reservation);
        return days;
    }, {});
});

const publicHolidaysByDay = computed(() => {
    return publicHolidays.value.reduce((days, publicHoliday) => {
        const dateKey = formatDatePart(new Date(publicHoliday.date));
        if (!days[dateKey]) {
            days[dateKey] = [];
        }
        days[dateKey].push(publicHoliday);
        return days;
    }, {});
});

const publicHolidayDates = computed(() => {
    return Object.keys(publicHolidaysByDay.value).filter((dateValue) => isHolidayBlocked(dateValue));
});

const mobileSelectedDay = computed(() => {
    return buildCalendarDay(parseDate(selectedDate.value));
});

const mobileSelectedDayTitle = computed(() => {
    return formatLongDate(parseDate(selectedDate.value));
});

const mobileDayEvents = computed(() => {
    return buildMobileReservationLayout(getReservationsForDay(selectedDate.value), selectedDate.value);
});

const mobileWeekDays = computed(() => {
    return dayColumns.value.map((day) => {
        return {
            ...day,
            reservations: getReservationsForDay(day.dateValue),
        };
    });
});

const mobileSelectedDayReservations = computed(() => {
    return getReservationsForDay(selectedDate.value);
});

const isTodaySelected = computed(() => {
    const todayValue = getTodayDate();
    const todayDate = parseDate(todayValue);

    if (calendarView.value === "day") {
        return selectedDate.value === todayValue
            && selectedFromDate.value === todayValue
            && selectedToDate.value === todayValue;
    }

    if (calendarView.value === "week") {
        return selectedDate.value === todayValue
            && selectedFromDate.value === formatDatePart(getStartOfWeek(todayDate))
            && selectedToDate.value === formatDatePart(getEndOfWorkWeek(todayDate));
    }

    return selectedDate.value === todayValue
        && selectedFromDate.value === formatDatePart(new Date(todayDate.getFullYear(), todayDate.getMonth(), 1))
        && selectedToDate.value === formatDatePart(new Date(todayDate.getFullYear(), todayDate.getMonth() + 1, 0));
});

onMounted(async () => {
    currentTimeIntervalId = window.setInterval(() => {
        currentDateTime.value = new Date();
    }, 60000);

    if (isAdmin.value) {
        getEmployees();
    }
    await ensureUserWorkplace();
    syncDateFiltersToView();
    search();
});

onUnmounted(() => {
    if (currentTimeIntervalId) {
        window.clearInterval(currentTimeIntervalId);
    }
});

const search = () => {
    loadMeetingRooms();
};

const loadMeetingRooms = async () => {
    params.value = {
        page: 1,
        itemsPerPage: 99999,
    };

    params.value["startAt[after]"] = `${formatDatePart(rangeStartDate.value)}T00:00:00`;
    params.value["endAt[before]"] = `${formatDatePart(rangeEndDate.value)}T23:59:59`;

    if (isAdmin.value && selectedEmployee.value) {
        params.value["employee.id"] = selectedEmployee.value;
    }

    params.value["order[startAt]"] = "ASC";

    uiStore.startLoading();
    try {
        const [meetingRoomsResponse, publicHolidaysResponse] = await Promise.all([
            MeetingRoomService.getMeetingRooms(params.value),
            getPublicHolidaysRequest(),
        ]);

        meetingRooms.value = meetingRoomsResponse?.data?.["hydra:member"] || [];
        publicHolidays.value = publicHolidaysResponse?.data?.["hydra:member"] || [];
    } catch {
        meetingRooms.value = [];
        publicHolidays.value = [];
    } finally {
        uiStore.stopLoading();
    }
};

const getPublicHolidaysRequest = () => {
    if (!userWorkplaceId.value && !isAdmin.value) {
        publicHolidays.value = [];
        return Promise.resolve(null);
    }

    const publicHolidayParams = {
        itemsPerPage: 99999,
        "date[after]": selectedFromDate.value,
        "date[before]": selectedToDate.value,
        "order[date]": "ASC",
    };

    if (userWorkplaceId.value) {
        publicHolidayParams["workplace.id"] = userWorkplaceId.value;
    }

    return PublicHolidayService.getPublicHolidays(publicHolidayParams);
};

const getEmployees = () => {
    const employeeParams = {
        itemsPerPage: 99999,
        active: true,
    };
    employeeParams["order[name]"] = "ASC";

    EmployeeService.getEmployees(employeeParams)
        .then((res) => {
            employees.value = res.data["hydra:member"];
            employeesOriginal.value = res.data["hydra:member"];
        })
        .catch(() => {
            employees.value = [];
            employeesOriginal.value = [];
        });
};

const ensureUserWorkplace = async () => {
    if (!user.value?.id || userWorkplaceId.value) {
        return;
    }

    await authStore.setUserLogged(user.value);
};

const setCalendarView = (view) => {
    calendarView.value = view;
    syncDateFiltersToView();
    search();
};

const goToday = () => {
    if (isTodaySelected.value) {
        return;
    }

    selectedDate.value = getTodayDate();
    syncDateFiltersToView();
    search();
};

const goPreviousPeriod = () => {
    movePeriod(-1);
};

const goNextPeriod = () => {
    movePeriod(1);
};

const movePeriod = (direction) => {
    const startDate = parseDate(selectedFromDate.value);

    if (calendarView.value === "day") {
        selectedDate.value = formatDatePart(addDays(startDate, direction));
    } else if (calendarView.value === "week") {
        selectedDate.value = formatDatePart(addDays(startDate, direction * 7));
    } else {
        const date = parseDate(selectedDate.value);
        selectedDate.value = formatDatePart(new Date(date.getFullYear(), date.getMonth() + direction, 1));
    }

    syncDateFiltersToView();
    search();
};

const handleFromDateChange = (value) => {
    if (!value) {
        return;
    }

    if (!selectedToDate.value || value > selectedToDate.value || calendarView.value === "day") {
        selectedToDate.value = value;
    }

    selectedDate.value = value;
    search();
};

const handleToDateChange = (value) => {
    if (!value) {
        return;
    }

    if (!selectedFromDate.value || value < selectedFromDate.value) {
        selectedFromDate.value = value;
    }

    if (value !== selectedFromDate.value && calendarView.value === "day") {
        calendarView.value = "week";
    }

    selectedDate.value = selectedFromDate.value;
    search();
};

const openReservationDialog = (dateValue = selectedDate.value, timeValue = null) => {
    if (isPastDate(dateValue)) {
        notifyPastDate();
        return;
    }

    if (isPastTimeSlot(dateValue, timeValue)) {
        return;
    }

    if (isHolidayBlocked(dateValue)) {
        return;
    }

    selectedReservation.value = null;
    selectedReservationReadonly.value = false;
    selectedDate.value = dateValue || selectedDate.value;
    selectedReservationStartAt.value = buildInitialStartAt(dateValue, timeValue);
    dialogNewReservation.value = true;
};

const openDefaultReservationDialog = () => {
    openReservationDialog(getTodayDate());
};

const openEditDialog = (meetingRoom) => {
    if (!canManageReservation(meetingRoom)) {
        return;
    }

    selectedReservation.value = meetingRoom;
    selectedReservationReadonly.value = !isReservationEditable(meetingRoom);
    selectedReservationStartAt.value = null;
    dialogNewReservation.value = true;
};

const handleRowClick = (meetingRoom) => {
    if (!canManageReservation(meetingRoom)) {
        return;
    }

    openEditDialog(meetingRoom);
};

const closeReservationDialog = () => {
    dialogNewReservation.value = false;
    selectedReservation.value = null;
    selectedReservationStartAt.value = null;
    selectedReservationReadonly.value = false;
};

const openDeleteDialog = (meetingRoom) => {
    if (!isReservationEditable(meetingRoom)) {
        return;
    }

    selectedReservation.value = meetingRoom;
    dialogDeleteReservation.value = true;
};

const closeDeleteDialog = () => {
    dialogDeleteReservation.value = false;
    selectedReservation.value = null;
};

const deleteReservation = () => {
    if (!selectedReservation.value?.id || !isReservationEditable(selectedReservation.value)) {
        return;
    }

    isDeletingReservation.value = true;
    MeetingRoomService.deleteMeetingRoom(selectedReservation.value.id).then((res) => {
        if (res && (res.status === 200 || res.status === 204)) {
            notification.addNotification({
                type: "success",
                message: "Reserva eliminada",
                duration: 4000,
            });
            closeDeleteDialog();
            search();
        }
    }).finally(() => {
        isDeletingReservation.value = false;
    });
};

const disableFromDates = (date) => {
    if (!selectedToDate.value) {
        return false;
    }

    return isAfterDateOnly(date, parseDate(selectedToDate.value));
};

const disableToDates = (date) => {
    if (!selectedFromDate.value) {
        return false;
    }

    return isAfterDateOnly(parseDate(selectedFromDate.value), date);
};

const getReservedBy = (meetingRoom) => {
    return (
        meetingRoom.employee?.fullName ||
        "-"
    );
};

const getSubject = (meetingRoom) => {
    return meetingRoom.subject || meetingRoom.description || "-";
};

const getReservationTime = (meetingRoom) => {
    if (!meetingRoom?.startAt || !meetingRoom?.endAt) {
        return "-";
    }

    return `${formatTime(new Date(meetingRoom.startAt))} - ${formatTime(new Date(meetingRoom.endAt))}`;
};

const getPublicHolidaysForDay = (dateValue) => {
    return publicHolidaysByDay.value[dateValue] || [];
};

const getPublicHolidayLabel = (dateValue) => {
    const holidays = getUniquePublicHolidayItems(dateValue);
    if (!holidays.length) {
        return "";
    }

    return holidays.map((holiday) => {
        if (isNationalPublicHoliday(holiday)) {
            return holiday.description;
        }

        if (isAdmin.value && holiday.workplaces.length) {
            return `${holiday.workplaces.join(", ")} - ${holiday.description}`;
        }

        const type = holiday.type ? `${capitalize(holiday.type)}: ` : "";
        return `${type}${holiday.description}`;
    }).join(" · ");
};

const getUniquePublicHolidayItems = (dateValue) => {
    const holidays = getPublicHolidaysForDay(dateValue);
    const groupedHolidays = new Map();

    holidays.forEach((holiday) => {
        const holidayDate = formatDatePart(new Date(holiday.date));
        const description = getPublicHolidayDescription(holiday);
        const key = `${holidayDate}-${normalizeText(description).toLowerCase()}`;
        const workplaceName = getPublicHolidayWorkplaceName(holiday);

        if (!groupedHolidays.has(key)) {
            groupedHolidays.set(key, {
                description,
                type: holiday.type,
                workplaces: [],
            });
        }

        if (workplaceName && !groupedHolidays.get(key).workplaces.includes(workplaceName)) {
            groupedHolidays.get(key).workplaces.push(workplaceName);
        }
    });

    return Array.from(groupedHolidays.values());
};

const getPublicHolidayDescription = (holiday) => {
    return holiday.description?.trim() || "Festivo";
};

const isNationalPublicHoliday = (holiday) => {
    return normalizeText(holiday.type || "").toLowerCase() === "nacional";
};

const getPublicHolidayWorkplaceName = (holiday) => {
    return (
        holiday.workplace?.name ||
        holiday.workPlaceName ||
        holiday.workplaceName ||
        ""
    );
};

const isPublicHoliday = (dateValue) => {
    return getPublicHolidaysForDay(dateValue).length > 0;
};

const isHolidayBlocked = (dateValue) => {
    const holidays = getPublicHolidaysForDay(dateValue);
    if (!holidays.length) {
        return false;
    }

    if (!isAdmin.value) {
        return true;
    }

    if (userWorkplaceId.value) {
        return true;
    }

    return holidays.some((holiday) => isNationalPublicHoliday(holiday));
};

const isHolidayInformative = (dateValue) => {
    return isPublicHoliday(dateValue) && isAdmin.value && !isHolidayBlocked(dateValue);
};

const isPastDate = (dateValue) => {
    return parseDate(dateValue).getTime() < parseDate(getTodayDate()).getTime();
};

const isPastTimeSlot = (dateValue, timeValue) => {
    if (!timeValue || dateValue !== getCurrentDateValue()) {
        return false;
    }

    return new Date(`${dateValue}T${timeValue}:00`).getTime() <= currentDateTime.value.getTime();
};

const notifyPastDate = () => {
    notification.addNotification({
        type: "warning",
        message: "No se pueden crear reservas en días anteriores a hoy.",
        duration: 4000,
    });
};

const getReservationsForDay = (dateValue) => {
    return reservationsByDay.value[dateValue] || [];
};

const getReservationsForSlot = (dateValue, timeValue) => {
    return getReservationsForDay(dateValue).filter((reservation) => {
        return reservationOverlapsSlot(reservation, dateValue, timeValue);
    });
};

const getSlotCellClass = (dateValue, timeValue) => {
    const slotReservations = getReservationsForSlot(dateValue, timeValue);

    return {
        holiday: isHolidayBlocked(dateValue),
        "holiday-info": isHolidayInformative(dateValue),
        past: isPastDate(dateValue),
        "past-time": isPastTimeSlot(dateValue, timeValue),
        occupied: slotReservations.length > 0,
        manageable: slotReservations.some((reservation) => canManageReservation(reservation)),
    };
};

const shouldShowCurrentTimeIndicator = (dateValue, timeValue) => {
    if (calendarView.value === "month" || dateValue !== getCurrentDateValue()) {
        return false;
    }

    const currentMinutes = getCurrentMinutes();
    const slotMinutes = getTimeValueMinutes(timeValue);

    return currentMinutes >= slotMinutes
        && currentMinutes < slotMinutes + 30
        && currentMinutes >= timelineStartMinutes
        && currentMinutes < timelineEndMinutes;
};

const getCurrentTimeIndicatorStyle = (timeValue) => {
    const currentMinutes = getCurrentMinutes();
    const slotMinutes = getTimeValueMinutes(timeValue);
    const progress = clampNumber((currentMinutes - slotMinutes) / 30, 0, 1);

    return {
        top: `${progress * 100}%`,
    };
};

const shouldShowMobileCurrentTimeIndicator = (dateValue) => {
    return calendarView.value === "day"
        && dateValue === getCurrentDateValue()
        && isCurrentTimeInTimeline();
};

const getMobileCurrentTimeIndicatorStyle = () => {
    const top = ((getCurrentMinutes() - timelineStartMinutes) / 30) * mobileSlotHeight;

    return {
        top: `${top}px`,
    };
};

const getCurrentTimeLabel = () => {
    return `Ahora ${formatTime(currentDateTime.value)}`;
};

const handleSlotClick = (dateValue, timeValue) => {
    const slotReservation = getReservationsForSlot(dateValue, timeValue)[0];

    if (slotReservation) {
        handleRowClick(slotReservation);
        return;
    }

    if (isPastDate(dateValue)) {
        return;
    }

    if (isPastTimeSlot(dateValue, timeValue)) {
        return;
    }

    if (isHolidayBlocked(dateValue)) {
        return;
    }

    openReservationDialog(dateValue, timeValue);
};

const getReservationSlotClass = (meetingRoom, dateValue, timeValue) => {
    return {
        manageable: canManageReservation(meetingRoom),
        continuation: !reservationStartsInSlot(meetingRoom, dateValue, timeValue),
        "continues-before": reservationStartsBeforeSlot(meetingRoom, dateValue, timeValue),
        "continues-after": reservationEndsAfterSlot(meetingRoom, dateValue, timeValue),
    };
};

const reservationStartsInSlot = (meetingRoom, dateValue, timeValue) => {
    const { start, end } = getSlotRange(dateValue, timeValue);
    const reservationStart = new Date(meetingRoom.startAt);

    return reservationStart >= start && reservationStart < end;
};

const isSelectedDay = (dateValue) => {
    return dateValue === selectedDate.value;
};

const selectDay = (dateValue) => {
    selectedDate.value = dateValue;

    if (calendarView.value === "month") {
        search();
        if (!isHolidayBlocked(dateValue) && !isPastDate(dateValue)) {
            openReservationDialog(dateValue);
        }
        return;
    }

    search();
};

const selectMobileMonthDay = (dateValue) => {
    selectedDate.value = dateValue;
};

const syncDateFiltersToView = () => {
    const date = parseDate(selectedDate.value);

    if (calendarView.value === "day") {
        selectedFromDate.value = formatDatePart(date);
        selectedToDate.value = formatDatePart(date);
        return;
    }

    if (calendarView.value === "week") {
        selectedFromDate.value = formatDatePart(getStartOfWeek(date));
        selectedToDate.value = formatDatePart(getEndOfWorkWeek(date));
        return;
    }

    selectedFromDate.value = formatDatePart(new Date(date.getFullYear(), date.getMonth(), 1));
    selectedToDate.value = formatDatePart(new Date(date.getFullYear(), date.getMonth() + 1, 0));
};

const canManageReservation = (meetingRoom) => {
    if (isAdmin.value) {
        return true;
    }

    const currentUserId = user.value?.id;
    if (!currentUserId) {
        return false;
    }

    return (
        meetingRoom.employee?.id === currentUserId ||
        meetingRoom.createdBy?.id === currentUserId ||
        meetingRoom.user?.id === currentUserId
    );
};

const hasReservationEnded = (meetingRoom) => {
    if (!meetingRoom?.endAt) {
        return false;
    }

    const now = Math.max(currentDateTime.value.getTime(), Date.now());
    return new Date(meetingRoom.endAt).getTime() <= now;
};

const isReservationEditable = (meetingRoom) => {
    return canManageReservation(meetingRoom) && !hasReservationEnded(meetingRoom);
};

const normalizeText = (text) => {
    return normalizeSearchText(text);
};

const filterUsers = (query) => {
    employees.value = filterByNormalizedText(employeesOriginal.value, query, employee => employee.fullName);
};

const getMobileEventStyle = (event) => {
    const columnWidth = 100 / event.columnCount;
    const left = event.column * columnWidth;

    return {
        top: `${event.top}px`,
        height: `${event.height}px`,
        left: `calc(${left}% + ${event.column > 0 ? 4 : 0}px)`,
        width: `calc(${columnWidth}% - ${event.columnCount > 1 ? 6 : 0}px)`,
    };
};

function buildMobileReservationLayout(reservations, dateValue) {
    const normalizedReservations = reservations
        .map((reservation) => {
            const reservationRange = getReservationMinutesForDay(reservation, dateValue);
            if (!reservationRange) {
                return null;
            }

            return {
                reservation,
                ...reservationRange,
            };
        })
        .filter(Boolean)
        .sort((first, second) => {
            if (first.startMinutes === second.startMinutes) {
                return first.endMinutes - second.endMinutes;
            }

            return first.startMinutes - second.startMinutes;
        });

    const lanes = [];

    normalizedReservations.forEach((event) => {
        const laneIndex = lanes.findIndex((laneEnd) => laneEnd <= event.startMinutes);
        const column = laneIndex === -1 ? lanes.length : laneIndex;
        lanes[column] = event.endMinutes;
        event.column = column;
    });

    const columnCount = Math.max(lanes.length, 1);

    return normalizedReservations.map((event) => {
        const visibleStart = clampNumber(event.startMinutes, timelineStartMinutes, timelineEndMinutes);
        const visibleEnd = clampNumber(event.endMinutes, timelineStartMinutes, timelineEndMinutes);
        const durationSlots = Math.max((visibleEnd - visibleStart) / 30, 1);

        return {
            ...event,
            columnCount,
            top: ((visibleStart - timelineStartMinutes) / 30) * mobileSlotHeight,
            height: durationSlots * mobileSlotHeight,
        };
    });
}

function getReservationMinutesForDay(reservation, dateValue) {
    if (!reservation?.startAt || !reservation?.endAt) {
        return null;
    }

    const reservationStart = new Date(reservation.startAt);
    const reservationEnd = new Date(reservation.endAt);

    if (Number.isNaN(reservationStart.getTime()) || Number.isNaN(reservationEnd.getTime())) {
        return null;
    }

    const dayStart = new Date(`${dateValue}T00:00:00`);
    const dayEnd = addDays(dayStart, 1);

    if (reservationEnd <= dayStart || reservationStart >= dayEnd) {
        return null;
    }

    const startsAt = reservationStart < dayStart ? dayStart : reservationStart;
    const endsAt = reservationEnd > dayEnd ? dayEnd : reservationEnd;
    const startMinutes = (startsAt.getHours() * 60) + startsAt.getMinutes();
    const endMinutes = (endsAt.getHours() * 60) + endsAt.getMinutes();

    if (endMinutes <= timelineStartMinutes || startMinutes >= timelineEndMinutes) {
        return null;
    }

    return {
        startMinutes,
        endMinutes,
    };
}

function clampNumber(value, min, max) {
    return Math.max(min, Math.min(max, value));
}

function getTodayDate() {
    const today = new Date();
    return formatDatePart(today);
}

function getCurrentDateValue() {
    return formatDatePart(currentDateTime.value);
}

function getCurrentMinutes() {
    return (currentDateTime.value.getHours() * 60) + currentDateTime.value.getMinutes();
}

function isCurrentTimeInTimeline() {
    const currentMinutes = getCurrentMinutes();
    return currentMinutes >= timelineStartMinutes && currentMinutes < timelineEndMinutes;
}

function getTimeValueMinutes(timeValue) {
    const [hours, minutes] = timeValue.split(":").map(Number);
    return (hours * 60) + minutes;
}

function buildInitialStartAt(dateValue, timeValue = null) {
    if (!dateValue) {
        return null;
    }

    const startTime = timeValue || getDefaultReservationTime(dateValue);
    return `${dateValue}T${startTime}:00`;
}

function getDefaultReservationTime(dateValue) {
    if (dateValue === getTodayDate()) {
        return formatTime(getNextStrictHalfHour(new Date()));
    }

    return "09:00";
}

function getNextStrictHalfHour(date) {
    const normalizedDate = new Date(date);
    normalizedDate.setSeconds(0, 0);
    const minutes = normalizedDate.getMinutes();

    if (minutes < 30) {
        normalizedDate.setMinutes(30);
    } else {
        normalizedDate.setHours(normalizedDate.getHours() + 1, 0);
    }

    return normalizedDate;
}

function buildTimeSlots() {
    const slots = [];
    for (let hour = 8; hour <= 20; hour += 1) {
        slots.push(`${String(hour).padStart(2, "0")}:00`);
        if (hour < 20) {
            slots.push(`${String(hour).padStart(2, "0")}:30`);
        }
    }

    return slots;
}

function buildCalendarDay(date) {
    return {
        date: new Date(date),
        dateValue: formatDatePart(date),
        dayName: capitalize(date.toLocaleDateString("es-ES", { weekday: "short" }).replace(".", "")),
        dayNumber: date.getDate(),
        isToday: formatDatePart(date) === getTodayDate(),
    };
}

function parseDate(dateValue) {
    if (!dateValue) {
        return new Date();
    }

    const [year, month, day] = dateValue.split("-").map(Number);
    return new Date(year, month - 1, day);
}

function getStartOfWeek(date) {
    const day = date.getDay() || 7;
    return addDays(date, 1 - day);
}

function getEndOfWeek(date) {
    return addDays(getStartOfWeek(date), 6);
}

function getEndOfWorkWeek(date) {
    return addDays(getStartOfWeek(date), 4);
}

function getDatesBetween(startDate, endDate) {
    const days = Math.max(getDaysDifference(startDate, endDate), 0) + 1;
    const visibleDays = Math.min(days, 14);

    return Array.from({ length: visibleDays }, (_, index) => addDays(startDate, index))
        .filter((date) => calendarView.value !== "week" || isWorkday(date));
}

function getMonthsBetween(startDate, endDate) {
    const startMonth = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const endMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
    const monthCount = (
        (endMonth.getFullYear() - startMonth.getFullYear()) * 12
        + endMonth.getMonth()
        - startMonth.getMonth()
    ) + 1;

    return Array.from({ length: Math.min(Math.max(monthCount, 1), 12) }, (_, index) => {
        return new Date(startMonth.getFullYear(), startMonth.getMonth() + index, 1);
    });
}

function getDaysDifference(startDate, endDate) {
    const firstDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const secondDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    return Math.round((secondDate.getTime() - firstDate.getTime()) / 86400000);
}

function addDays(date, days) {
    const nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + days);
    return nextDate;
}

function formatDatePart(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function formatMonthKey(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
}

function formatDateOnly(date) {
    return date.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
}

function formatLongDate(date) {
    return capitalize(date.toLocaleDateString("es-ES", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
    }));
}

function formatMonthYear(date) {
    return date.toLocaleDateString("es-ES", {
        month: "long",
        year: "numeric",
    });
}

function formatTime(date) {
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
}

function getSlotRange(dateValue, timeValue) {
    const start = new Date(`${dateValue}T${timeValue}:00`);
    const end = new Date(start);
    end.setMinutes(end.getMinutes() + 30);

    return { start, end };
}

function reservationOverlapsSlot(meetingRoom, dateValue, timeValue) {
    if (!meetingRoom?.startAt || !meetingRoom?.endAt) {
        return false;
    }

    const { start, end } = getSlotRange(dateValue, timeValue);
    const reservationStart = new Date(meetingRoom.startAt);
    const reservationEnd = new Date(meetingRoom.endAt);

    return reservationStart < end && reservationEnd > start;
}

function reservationStartsBeforeSlot(meetingRoom, dateValue, timeValue) {
    const { start } = getSlotRange(dateValue, timeValue);
    return new Date(meetingRoom.startAt) < start;
}

function reservationEndsAfterSlot(meetingRoom, dateValue, timeValue) {
    const { end } = getSlotRange(dateValue, timeValue);
    return new Date(meetingRoom.endAt) > end;
}

function isWorkday(date) {
    const day = date.getDay();
    return day >= 1 && day <= 5;
}

function isSameMonth(dateA, dateB) {
    return dateA.getFullYear() === dateB.getFullYear() && dateA.getMonth() === dateB.getMonth();
}

function isDateInRange(date, startDate, endDate) {
    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    const normalizedStart = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()).getTime();
    const normalizedEnd = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()).getTime();

    return normalizedDate >= normalizedStart && normalizedDate <= normalizedEnd;
}

function isAfterDateOnly(dateA, dateB) {
    const firstDate = new Date(dateA.getFullYear(), dateA.getMonth(), dateA.getDate());
    const secondDate = new Date(dateB.getFullYear(), dateB.getMonth(), dateB.getDate());
    return firstDate.getTime() > secondDate.getTime();
}

function capitalize(text) {
    if (!text) {
        return "";
    }

    return text.charAt(0).toUpperCase() + text.slice(1);
}
</script>

<template>
    <div>
        <HeaderTitle title="Comunicación" />
        <div class="d-flex row meeting-room-header">
            <div class="d-flex row justify-content-start el-col-xs-24 col-md-6">
                <div class="col-md-12 d-flex align-items-center">
                    <IconMeetingRoomReservation class="iconMeeting" />
                    <div class="title-text ml-4">
                        Reserva de sala
                    </div>
                </div>
            </div>
        </div>

        <el-row class="mt-2">
            <el-card class="card-search card-abs w-100 wback" body-style="padding:0">
                <div class="search-form">
                    <div class="row meeting-room-search-row"
                        :class="{ 'meeting-room-search-row--no-employee': !isAdmin }">
                        <div class="col-md-2 el-col-xs-24 meeting-room-view-col">
                            <div class="form-item label-common meeting-room-filter-item">
                                <label class="mb-2 w-100">Vista</label>
                                <div class="meeting-room-view-toggle">
                                    <button v-for="mode in viewModes" :key="mode.value" type="button"
                                        class="meeting-room-view-button"
                                        :class="{ active: calendarView === mode.value }"
                                        @click="setCalendarView(mode.value)">
                                        {{ mode.label }}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-2 el-col-xs-24 meeting-room-date-from-col">
                            <div class="form-item date-picker label-common meeting-room-filter-item">
                                <label class="mb-2 w-100" for="dateFrom">Desde</label>
                                <el-date-picker id="dateFrom" v-model="selectedFromDate" class="w-100" type="date"
                                    format="DD/MM/YYYY" value-format="YYYY-MM-DD" :clearable="false"
                                    :disabled-date="disableFromDates" @change="handleFromDateChange" />
                            </div>
                        </div>
                        <div class="col-md-2 el-col-xs-24 meeting-room-date-to-col">
                            <div class="form-item date-picker label-common meeting-room-filter-item">
                                <label class="mb-2 w-100" for="dateTo">Hasta</label>
                                <el-date-picker id="dateTo" v-model="selectedToDate" class="w-100" type="date"
                                    format="DD/MM/YYYY" value-format="YYYY-MM-DD" :clearable="false"
                                    :disabled-date="disableToDates" @change="handleToDateChange" />
                            </div>
                        </div>
                        <div v-if="isAdmin" class="col-md-3 el-col-xs-24 meeting-room-employee-col">
                            <div class="form-item label-common meeting-room-filter-item">
                                <label class="mb-2 w-100" for="employee">Empleado</label>
                                <el-select v-model="selectedEmployee" id="employee" class="w-100" placeholder="Todos"
                                    filterable clearable :filter-method="filterUsers" @change="search">
                                    <el-option v-for="employee in employees" :key="employee.id"
                                        :label="employee.fullName" :value="employee.id" />
                                </el-select>
                            </div>
                        </div>
                        <div class="col-md-3 el-col-xs-24 meeting-room-actions-col">
                            <div class="d-flex justify-content-end align-items-end meeting-room-buttons">
                                <div class="meeting-room-button-col">
                                    <button type="button" @click="openDefaultReservationDialog()"
                                        class="btn btn_red btn__new">
                                        Reservar
                                    </button>
                                </div>
                                <div class="meeting-room-button-col">
                                    <button type="button" @click="search()" class="btn btn_yellow btn__search">
                                        Buscar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </el-card>
        </el-row>

        <div class="meeting-room-calendar-shell mt-4"
            :class="{ 'meeting-room-calendar-shell--mobile-month': calendarView === 'month' }">
            <div class="meeting-room-calendar-toolbar">
                <div>
                    <div class="meeting-room-period-title">{{ currentPeriodTitle }}</div>
                    <div v-if="selectedPublicHolidayLabel" class="meeting-room-period-holiday">
                        Festivo · {{ selectedPublicHolidayLabel }}
                    </div>
                </div>
                <div class="meeting-room-period-actions">
                    <button type="button" class="meeting-room-icon-button previous" title="Periodo anterior"
                        aria-label="Periodo anterior" @click="goPreviousPeriod"></button>
                    <button type="button" class="btn btn_yellow meeting-room-today-button" :disabled="isTodaySelected"
                        @click="goToday">
                        Hoy
                    </button>
                    <button type="button" class="meeting-room-icon-button next" title="Periodo siguiente"
                        aria-label="Periodo siguiente" @click="goNextPeriod"></button>
                </div>
            </div>

            <div class="meeting-room-desktop-calendar">
                <div v-if="calendarView !== 'month'" class="meeting-room-time-calendar"
                    :style="{ '--meeting-room-days': dayColumns.length }">
                    <div v-if="calendarView !== 'day'" class="meeting-room-time-header">
                        <div class="meeting-room-time-corner"></div>
                        <div v-for="day in dayColumns" :key="day.dateValue" class="meeting-room-day-heading" :class="{
                            selected: isSelectedDay(day.dateValue), today: day.isToday,
                            holiday: isHolidayBlocked(day.dateValue),
                            'holiday-info': isHolidayInformative(day.dateValue),
                            past: isPastDate(day.dateValue)
                        }" @click="selectDay(day.dateValue)">
                            <span>{{ day.dayName }}</span>
                            <strong>{{ day.dayNumber }}</strong>
                            <small v-if="isPublicHoliday(day.dateValue)" class="meeting-room-week-holiday-label">
                                Festivo · {{ getPublicHolidayLabel(day.dateValue) }}
                            </small>
                        </div>
                    </div>
                    <div class="meeting-room-time-body">
                        <template v-for="slot in timeSlots" :key="slot">
                            <div class="meeting-room-time-label">{{ slot }}</div>
                            <div v-for="day in dayColumns" :key="`${day.dateValue}-${slot}`"
                                class="meeting-room-time-cell" :class="getSlotCellClass(day.dateValue, slot)"
                                @click="handleSlotClick(day.dateValue, slot)">
                                <div v-if="shouldShowCurrentTimeIndicator(day.dateValue, slot)"
                                    class="meeting-room-current-time-indicator"
                                    :style="getCurrentTimeIndicatorStyle(slot)">
                                    <span>{{ getCurrentTimeLabel() }}</span>
                                </div>
                                <button v-if="calendarView !== 'day' && !isHolidayBlocked(day.dateValue)
                                    && !isPastDate(day.dateValue) && !isPastTimeSlot(day.dateValue, slot)"
                                    type="button" class="meeting-room-cell-plus" title="Crear reserva"
                                    @click.stop="openReservationDialog(day.dateValue, slot)">
                                    <font-awesome-icon icon="plus" />
                                </button>
                                <div v-for="meetingRoom in getReservationsForSlot(day.dateValue, slot)"
                                    :key="meetingRoom.id" class="meeting-room-event"
                                    :class="getReservationSlotClass(meetingRoom, day.dateValue, slot)"
                                    @click.stop="handleRowClick(meetingRoom)">
                                    <strong v-if="reservationStartsInSlot(meetingRoom, day.dateValue, slot)">
                                        {{ getReservationTime(meetingRoom) }}
                                    </strong>
                                    <span v-if="reservationStartsInSlot(meetingRoom, day.dateValue, slot)">
                                        {{ getSubject(meetingRoom) }}
                                    </span>
                                    <small v-if="reservationStartsInSlot(meetingRoom, day.dateValue, slot)">
                                        {{ getReservedBy(meetingRoom) }}
                                    </small>
                                    <button v-if="reservationStartsInSlot(meetingRoom, day.dateValue, slot)
                                        && isReservationEditable(meetingRoom)" type="button"
                                        class="meeting-room-event-delete" title="Eliminar reserva"
                                        @click.stop="openDeleteDialog(meetingRoom)">
                                        <font-awesome-icon icon="times" />
                                    </button>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>

                <div v-else class="meeting-room-months">
                    <div v-for="monthCalendar in monthCalendars" :key="monthCalendar.key"
                        class="meeting-room-month-section">
                        <h4 class="meeting-room-month-title">{{ monthCalendar.title }}</h4>
                        <div class="meeting-room-month-calendar">
                            <div v-for="label in ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']" :key="label"
                                class="meeting-room-month-weekday">
                                {{ label }}
                            </div>
                            <div v-for="day in monthCalendar.days" :key="`${monthCalendar.key}-${day.dateValue}`"
                                class="meeting-room-month-day" :class="{
                                    muted: !day.currentMonth || !day.inSelectedRange,
                                    selected: isSelectedDay(day.dateValue), today: day.isToday,
                                    holiday: isHolidayBlocked(day.dateValue),
                                    'holiday-info': isHolidayInformative(day.dateValue),
                                    past: isPastDate(day.dateValue)
                                }" @click="selectDay(day.dateValue)">
                                <div class="meeting-room-month-day-top">
                                    <span>{{ day.dayNumber }}</span>
                                    <button v-if="!isHolidayBlocked(day.dateValue) && !isPastDate(day.dateValue)"
                                        type="button" title="Crear reserva"
                                        @click.stop="openReservationDialog(day.dateValue)">
                                        <font-awesome-icon icon="plus" />
                                    </button>
                                </div>
                                <div v-if="isPublicHoliday(day.dateValue)" class="meeting-room-month-holiday">
                                    {{ getPublicHolidayLabel(day.dateValue) }}
                                </div>
                                <div class="meeting-room-month-events">
                                    <div v-for="meetingRoom in getReservationsForDay(day.dateValue).slice(0, 3)"
                                        :key="meetingRoom.id" class="meeting-room-month-event"
                                        :class="{ manageable: canManageReservation(meetingRoom) }"
                                        @click.stop="handleRowClick(meetingRoom)">
                                        <strong>{{ getReservationTime(meetingRoom) }}</strong>
                                        <span>{{ getSubject(meetingRoom) }}</span>
                                    </div>
                                    <small v-if="getReservationsForDay(day.dateValue).length > 3"
                                        class="meeting-room-more-events">
                                        +{{ getReservationsForDay(day.dateValue).length - 3 }} más
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="meeting-room-mobile-calendar">
                <div v-if="calendarView === 'day'" class="meeting-room-mobile-day">
                    <div class="meeting-room-mobile-timeline">
                        <div class="meeting-room-mobile-time-labels">
                            <div v-for="slot in timeSlots" :key="`mobile-label-${slot}`"
                                class="meeting-room-mobile-time-label">
                                {{ slot }}
                            </div>
                        </div>
                        <div class="meeting-room-mobile-slot-column">
                            <button v-for="slot in timeSlots" :key="`mobile-slot-${slot}`" type="button"
                                class="meeting-room-mobile-slot" :class="{
                                    holiday: isHolidayBlocked(mobileSelectedDay.dateValue),
                                    'holiday-info': isHolidayInformative(mobileSelectedDay.dateValue),
                                    past: isPastDate(mobileSelectedDay.dateValue),
                                    'past-time': isPastTimeSlot(mobileSelectedDay.dateValue, slot)
                                }" :disabled="isHolidayBlocked(mobileSelectedDay.dateValue)
                                    || isPastDate(mobileSelectedDay.dateValue)
                                    || isPastTimeSlot(mobileSelectedDay.dateValue, slot)"
                                @click="openReservationDialog(mobileSelectedDay.dateValue, slot)">
                            </button>
                            <div class="meeting-room-mobile-events-layer">
                                <div v-if="shouldShowMobileCurrentTimeIndicator(mobileSelectedDay.dateValue)"
                                    class="meeting-room-current-time-indicator meeting-room-current-time-indicator--mobile"
                                    :style="getMobileCurrentTimeIndicatorStyle()">
                                    <span>{{ getCurrentTimeLabel() }}</span>
                                </div>
                                <div v-for="event in mobileDayEvents" :key="event.reservation.id"
                                    class="meeting-room-mobile-event"
                                    :class="{ manageable: canManageReservation(event.reservation) }"
                                    :style="getMobileEventStyle(event)" @click.stop="handleRowClick(event.reservation)">
                                    <strong>{{ getReservationTime(event.reservation) }}</strong>
                                    <span>{{ getSubject(event.reservation) }}</span>
                                    <small>{{ getReservedBy(event.reservation) }}</small>
                                    <button v-if="isReservationEditable(event.reservation)" type="button"
                                        class="meeting-room-mobile-event-delete" title="Eliminar reserva"
                                        @click.stop="openDeleteDialog(event.reservation)">
                                        <font-awesome-icon icon="times" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else-if="calendarView === 'week'" class="meeting-room-mobile-week">
                    <div class="meeting-room-mobile-day-tabs">
                        <button v-for="day in mobileWeekDays" :key="`mobile-week-${day.dateValue}`" type="button"
                            :class="{
                                selected: isSelectedDay(day.dateValue), today: day.isToday,
                                holiday: isHolidayBlocked(day.dateValue),
                                'holiday-info': isHolidayInformative(day.dateValue),
                                past: isPastDate(day.dateValue)
                            }" @click="selectDay(day.dateValue)">
                            <span>{{ day.dayName }}</span>
                            <strong>{{ day.dayNumber }}</strong>
                        </button>
                    </div>

                    <div class="meeting-room-mobile-agenda">
                        <div v-for="day in mobileWeekDays" :key="`mobile-agenda-${day.dateValue}`"
                            class="meeting-room-mobile-agenda-day" :class="{ selected: isSelectedDay(day.dateValue) }">
                            <div class="meeting-room-mobile-agenda-header">
                                <div>
                                    <span>{{ day.dayName }} {{ day.dayNumber }}</span>
                                    <strong>{{ formatDateOnly(day.date) }}</strong>
                                </div>
                                <small v-if="isPublicHoliday(day.dateValue)" class="meeting-room-mobile-holiday-label">
                                    Festivo · {{ getPublicHolidayLabel(day.dateValue) }}
                                </small>
                                <button v-if="!isHolidayBlocked(day.dateValue) && !isPastDate(day.dateValue)"
                                    type="button" title="Crear reserva" @click="openReservationDialog(day.dateValue)">
                                    <font-awesome-icon icon="plus" />
                                </button>
                            </div>
                            <div v-if="day.reservations.length" class="meeting-room-mobile-event-list">
                                <div v-for="meetingRoom in day.reservations" :key="meetingRoom.id"
                                    class="meeting-room-mobile-agenda-card"
                                    :class="{ manageable: canManageReservation(meetingRoom) }"
                                    @click="handleRowClick(meetingRoom)">
                                    <strong>{{ getReservationTime(meetingRoom) }}</strong>
                                    <span>{{ getSubject(meetingRoom) }}</span>
                                    <small>{{ getReservedBy(meetingRoom) }}</small>
                                    <button v-if="isReservationEditable(meetingRoom)" type="button"
                                        title="Eliminar reserva" @click.stop="openDeleteDialog(meetingRoom)">
                                        <font-awesome-icon icon="times" />
                                    </button>
                                </div>
                            </div>
                            <div v-else class="meeting-room-mobile-empty">
                                Sin reservas
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else class="meeting-room-mobile-month">
                    <div v-for="monthCalendar in monthCalendars" :key="`mobile-${monthCalendar.key}`"
                        class="meeting-room-mobile-month-section">
                        <h4>{{ monthCalendar.title }}</h4>
                        <div class="meeting-room-mobile-month-grid">
                            <div v-for="label in ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']" :key="label"
                                class="meeting-room-mobile-month-weekday">
                                {{ label }}
                            </div>
                            <button v-for="day in monthCalendar.days"
                                :key="`mobile-${monthCalendar.key}-${day.dateValue}`" type="button"
                                class="meeting-room-mobile-month-day" :class="{
                                    muted: !day.currentMonth || !day.inSelectedRange,
                                    selected: isSelectedDay(day.dateValue), today: day.isToday,
                                    holiday: isHolidayBlocked(day.dateValue),
                                    'holiday-info': isHolidayInformative(day.dateValue),
                                    past: isPastDate(day.dateValue)
                                }" @click="selectMobileMonthDay(day.dateValue)">
                                <span>{{ day.dayNumber }}</span>
                                <b v-if="isPublicHoliday(day.dateValue)">F</b>
                                <i v-if="getReservationsForDay(day.dateValue).length"></i>
                            </button>
                        </div>
                    </div>

                    <div class="meeting-room-mobile-month-detail">
                        <div class="meeting-room-mobile-month-handle"></div>
                        <div class="meeting-room-mobile-agenda-header">
                            <div>
                                <span>{{ mobileSelectedDayTitle }}</span>
                                <strong>{{ mobileSelectedDayReservations.length }} reservas</strong>
                            </div>
                            <button type="button" class="meeting-room-mobile-month-toggle" title="Mostrar reservas"
                                aria-label="Mostrar reservas">
                                <font-awesome-icon icon="caret-up" />
                            </button>
                        </div>
                        <div v-if="isPublicHoliday(mobileSelectedDay.dateValue)"
                            class="meeting-room-mobile-month-holiday">
                            Festivo · {{ getPublicHolidayLabel(mobileSelectedDay.dateValue) }}
                        </div>
                        <button v-if="!isHolidayBlocked(mobileSelectedDay.dateValue)
                            && !isPastDate(mobileSelectedDay.dateValue)" type="button"
                            class="meeting-room-mobile-month-reserve"
                            @click="openReservationDialog(mobileSelectedDay.dateValue)">
                            Reservar en este día
                        </button>
                        <div v-if="mobileSelectedDayReservations.length" class="meeting-room-mobile-event-list">
                            <div v-for="meetingRoom in mobileSelectedDayReservations" :key="meetingRoom.id"
                                class="meeting-room-mobile-agenda-card"
                                :class="{ manageable: canManageReservation(meetingRoom) }"
                                @click="handleRowClick(meetingRoom)">
                                <strong>{{ getReservationTime(meetingRoom) }}</strong>
                                <span>{{ getSubject(meetingRoom) }}</span>
                                <small>{{ getReservedBy(meetingRoom) }}</small>
                                <button v-if="isReservationEditable(meetingRoom)" type="button" title="Eliminar reserva"
                                    @click.stop="openDeleteDialog(meetingRoom)">
                                    <font-awesome-icon icon="times" />
                                </button>
                            </div>
                        </div>
                        <div v-else-if="!isPublicHoliday(mobileSelectedDay.dateValue)"
                            class="meeting-room-mobile-empty">
                            Sin reservas para este día
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <el-dialog :model-value="dialogNewReservation" :center="true" :lock-scroll="true" :show-close="false"
            :width="750" class="meeting-room-dialog" @close="closeReservationDialog()" style="border-radius: 20px;">
            <MeetingRoomReservationDialog @close="closeReservationDialog()" @finish="search()"
                :meetingRoom="selectedReservation" :initial-start-at="selectedReservationStartAt"
                :public-holiday-dates="publicHolidayDates" :readonly="selectedReservationReadonly"
                v-if="dialogNewReservation" />
        </el-dialog>
        <SharedDeleteConfirmDialog v-model="dialogDeleteReservation" title="Eliminar reserva"
            message="¿Seguro que quieres eliminar esta reserva?" :loading="isDeletingReservation"
            @confirm="deleteReservation" @cancel="selectedReservation = null" />
    </div>
</template>

<style scoped lang="scss">
.card-search {
    border-radius: 15px;
    height: 150px;
    background: url("../../assets/images/meeting-room.png"), white;
    background-repeat: no-repeat;
    background-position-x: 100%;
    background-position-y: 69%;
    background-size: 26%;
    overflow: visible !important;
}

.iconMeeting {
    width: 40px;
    fill: $bg-blue;
}

.meeting-room-search-row {
    align-items: flex-end;
    row-gap: 12px;
}

.meeting-room-search-row>div,
.meeting-room-filter-item {
    min-width: 0;
}

.meeting-room-header .title-text {
    font-size: clamp(2.6rem, 3.1vw, 4.2rem);
    line-height: 1.05;
}

.meeting-room-filter-item {
    margin-bottom: 0;
}

.meeting-room-filter-item label {
    min-height: 24px;
    display: flex;
    align-items: flex-end;
}

.meeting-room-view-toggle {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    height: 44px;
    gap: 3px;
    padding: 3px;
    border: 1px solid #111;
    border-radius: 22px;
    background: #fff;
}

.meeting-room-view-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    justify-self: center;
    width: calc(100% - 4px);
    min-width: 0;
    box-sizing: border-box;
    border: 0;
    border-radius: 18px;
    background: transparent;
    color: $bg-blue;
    font-weight: 800;
    line-height: 1;
    transition: background-color 0.18s ease, color 0.18s ease;
}

.meeting-room-view-button.active {
    background: $bg-blue;
    color: #fff;
}

.meeting-room-view-button:first-child {
    transform: translateX(4px);
}

.meeting-room-view-button:last-child {
    transform: translateX(3px);
}

.meeting-room-actions-col {
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
}

.meeting-room-search-row--no-employee .meeting-room-actions-col {
    margin-left: auto;
}

.meeting-room-buttons {
    width: 100%;
    gap: 12px;
    margin-top: 0;
    justify-content: flex-end;
    align-items: center;
}

.meeting-room-button-col {
    width: 160px;
    display: flex;
    justify-content: center;
}

.btn__search,
.btn__new {
    width: 100%;
    text-transform: uppercase;
    font-weight: bolder;
    height: 40px;
}

.btn__search {
    color: $bg-blue;
}

.btn__new {
    color: white;
}

.meeting-room-calendar-shell {
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 8px 22px rgba(71, 76, 230, 0.12);
}

.meeting-room-desktop-calendar {
    max-width: 100%;
    min-width: 0;
}

.meeting-room-calendar-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 18px 20px;
    border-bottom: 1px solid rgba(71, 76, 230, 0.14);
}

.meeting-room-period-title {
    color: $bg-blue;
    font-size: 26px;
    font-weight: 900;
    line-height: 1.1;
}

.meeting-room-period-holiday {
    display: inline-flex;
    align-items: center;
    margin-top: 8px;
    padding: 5px 10px;
    border-radius: 16px;
    background: #f1f2f8;
    color: #ff0d66;
    font-size: 13px;
    font-weight: 900;
    line-height: 1.2;
}

.meeting-room-period-summary {
    margin-top: 4px;
    color: #374151;
    font-size: 15px;
}

.meeting-room-period-actions {
    display: inline-flex;
    align-items: center;
    gap: 10px;
}

.meeting-room-icon-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    min-width: 42px;
    max-width: 42px;
    height: 42px;
    min-height: 42px;
    max-height: 42px;
    flex: 0 0 42px;
    aspect-ratio: 1 / 1;
    border: 1px solid $bg-blue;
    border-radius: 50%;
    background: #fff;
    color: $bg-blue;
    padding: 0;
}

.meeting-room-icon-button::before {
    content: "";
    display: block;
    width: 11px;
    height: 11px;
    border-color: $bg-blue;
    border-style: solid;
    transform: rotate(45deg);
}

.meeting-room-icon-button.previous::before {
    margin-left: 4px;
    border-width: 0 0 2px 2px;
}

.meeting-room-icon-button.next::before {
    margin-right: 4px;
    border-width: 2px 2px 0 0;
}

.meeting-room-today-button {
    min-width: 90px;
    height: 40px;
    color: $bg-blue;
    font-weight: 800;
    text-transform: uppercase;
}

.meeting-room-today-button:disabled {
    background: #e6e6f7 !important;
    border-color: #e6e6f7 !important;
    color: #9ca3af !important;
    cursor: not-allowed;
    opacity: 1;
}

.meeting-room-time-calendar {
    max-width: 100%;
    min-width: 0;
    overflow-x: auto;
}

.meeting-room-time-header {
    display: grid;
    grid-template-columns: 86px repeat(var(--meeting-room-days), minmax(120px, 1fr));
    min-width: 820px;
    border-bottom: 1px solid rgba(71, 76, 230, 0.12);
}

.meeting-room-time-corner {
    background: #f7f7ff;
}

.meeting-room-day-heading {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;
    min-height: 68px;
    border-left: 1px solid rgba(71, 76, 230, 0.12);
    color: $bg-blue;
    cursor: pointer;
}

.meeting-room-day-heading span {
    font-weight: 800;
}

.meeting-room-day-heading strong {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: #f4f3ff;
    font-size: 18px;
}

.meeting-room-day-heading.selected strong {
    background: $bg-blue;
    color: #fff;
}

.meeting-room-day-heading.today strong {
    background: #ff0d66;
    color: #fff;
    border: 0;
}

.meeting-room-day-heading.holiday {
    background: #f1f2f8;
    color: #6b7280;
}

.meeting-room-day-heading.holiday-info {
    background: #fff7fb;
}

.meeting-room-day-heading.past:not(.holiday) {
    background: #fafaff;
    color: #9ca3af;
}

.meeting-room-week-holiday-label {
    flex: 0 0 calc(100% - 16px);
    max-width: calc(100% - 16px);
    padding: 3px 6px;
    border-radius: 10px;
    background: #fff;
    color: #ff0d66;
    font-size: 10px;
    font-weight: 900;
    line-height: 1.15;
    text-align: center;
    white-space: normal;
}

.meeting-room-time-body {
    display: grid;
    grid-template-columns: 86px repeat(var(--meeting-room-days), minmax(120px, 1fr));
    min-width: 820px;
}

.meeting-room-time-label {
    min-height: 56px;
    padding: 12px 12px 0 0;
    border-top: 1px solid rgba(71, 76, 230, 0.08);
    background: #f7f7ff;
    color: #4b5563;
    font-size: 13px;
    font-weight: 800;
    text-align: right;
}

.meeting-room-time-cell {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-height: 56px;
    padding: 6px;
    border-top: 1px solid rgba(71, 76, 230, 0.08);
    border-left: 1px solid rgba(71, 76, 230, 0.08);
    background: #fff;
    cursor: pointer;
}

.meeting-room-time-cell:hover {
    background: #fbfbff;
}

.meeting-room-time-cell.occupied {
    padding-top: 0;
    padding-bottom: 0;
    cursor: default;
}

.meeting-room-time-cell.holiday {
    background: #f1f2f8;
    cursor: default;
}

.meeting-room-time-cell.holiday-info {
    background: #fff;
}

.meeting-room-time-cell.past:not(.occupied):not(.holiday) {
    background: #fafaff;
    cursor: default;
}

.meeting-room-time-cell.past-time:not(.occupied):not(.holiday) {
    background: #fafaff;
    cursor: default;
}

.meeting-room-time-cell.holiday:hover {
    background: #f1f2f8;
}

.meeting-room-time-cell.holiday-info:hover {
    background: #fbfbff;
}

.meeting-room-time-cell.past:not(.occupied):not(.holiday):hover {
    background: #fafaff;
}

.meeting-room-time-cell.past-time:not(.occupied):not(.holiday):hover {
    background: #fafaff;
}

.meeting-room-time-cell.occupied.manageable {
    cursor: pointer;
}

.meeting-room-cell-plus {
    position: absolute;
    right: 8px;
    top: 7px;
    display: none;
    width: 22px;
    height: 22px;
    border: 0;
    border-radius: 50%;
    background: #ff0d66;
    color: #fff;
    font-size: 11px;
}

.meeting-room-time-cell:hover .meeting-room-cell-plus {
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.meeting-room-time-cell.occupied:hover .meeting-room-cell-plus {
    display: none;
}

.meeting-room-current-time-indicator {
    position: absolute;
    left: 0;
    right: 0;
    z-index: 8;
    border-top: 2px solid #ff0d66;
    pointer-events: none;
}

.meeting-room-current-time-indicator::before {
    content: "";
    position: absolute;
    left: -4px;
    top: -5px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #ff0d66;
}

.meeting-room-current-time-indicator span {
    position: absolute;
    left: 8px;
    top: -14px;
    max-width: calc(100% - 16px);
    padding: 2px 7px;
    border-radius: 12px;
    background: #ff0d66;
    color: #fff;
    font-size: 10px;
    font-weight: 900;
    line-height: 1.2;
    white-space: nowrap;
}

.meeting-room-event,
.meeting-room-month-event {
    display: flex;
    flex-direction: column;
    gap: 2px;
    border-left: 4px solid $bg-blue;
    border-radius: 8px;
    background: #eef0ff;
    color: #111827;
    line-height: 1.15;
}

.meeting-room-event {
    position: relative;
    flex: 1;
    min-height: 42px;
    padding: 8px 28px 8px 9px;
}

.meeting-room-event.continuation {
    min-height: 56px;
    padding: 0;
}

.meeting-room-event.continues-before {
    margin-top: -1px;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
}

.meeting-room-event.continues-after {
    margin-bottom: -1px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
}

.meeting-room-event.manageable,
.meeting-room-month-event.manageable {
    cursor: pointer;
}

.meeting-room-event strong,
.meeting-room-month-event strong {
    color: $bg-blue;
    font-size: 12px;
}

.meeting-room-event span,
.meeting-room-month-event span {
    font-size: 13px;
    font-weight: 800;
    word-break: break-word;
}

.meeting-room-event small {
    color: #4b5563;
    font-size: 11px;
}

.meeting-room-event-delete {
    position: absolute;
    top: 6px;
    right: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: 0;
    border-radius: 50%;
    background: #ff0d66;
    color: #fff;
    font-size: 10px;
}

.meeting-room-months {
    display: flex;
    flex-direction: column;
    gap: 22px;
    padding: 18px;
}

.meeting-room-month-section {
    overflow: hidden;
    border: 1px solid rgba(71, 76, 230, 0.12);
    border-radius: 14px;
    background: #fff;
}

.meeting-room-month-title {
    margin: 0;
    padding: 14px 16px;
    border-bottom: 1px solid rgba(71, 76, 230, 0.12);
    color: $bg-blue;
    font-size: 20px;
    font-weight: 900;
}

.meeting-room-month-calendar {
    display: grid;
    grid-template-columns: repeat(7, minmax(120px, 1fr));
    max-width: 100%;
    min-width: 0;
    overflow-x: auto;
}

.meeting-room-month-weekday {
    padding: 14px 12px;
    border-bottom: 1px solid rgba(71, 76, 230, 0.12);
    color: $bg-blue;
    font-weight: 900;
    text-align: center;
}

.meeting-room-month-day {
    min-height: 142px;
    padding: 10px;
    border-right: 1px solid rgba(71, 76, 230, 0.08);
    border-bottom: 1px solid rgba(71, 76, 230, 0.08);
    background: #fff;
    cursor: pointer;
}

.meeting-room-month-day.muted {
    background: #f9f9ff;
    color: #9ca3af;
}

.meeting-room-month-day.muted .meeting-room-month-day-top button {
    opacity: 0.45;
}

.meeting-room-month-day.holiday {
    background: #f1f2f8;
    cursor: default;
}

.meeting-room-month-day.holiday-info {
    background: #fff7fb;
}

.meeting-room-month-day.past:not(.holiday) {
    background: #fafaff;
}

.meeting-room-month-holiday {
    margin-bottom: 6px;
    padding: 4px 6px;
    border-radius: 6px;
    background: #fff;
    color: #ff0d66;
    font-size: 11px;
    font-weight: 800;
    line-height: 1.2;
}

.meeting-room-month-day.selected {
    box-shadow: inset 0 0 0 2px $bg-blue;
}

.meeting-room-month-day.today .meeting-room-month-day-top span {
    background: #ff0d66;
    color: #fff;
}

.meeting-room-month-day-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
}

.meeting-room-month-day-top span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    color: $bg-blue;
    font-weight: 900;
}

.meeting-room-month-day-top button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border: 0;
    border-radius: 50%;
    background: #ff0d66;
    color: #fff;
    font-size: 11px;
}

.meeting-room-month-events {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.meeting-room-month-event {
    padding: 6px 7px;
}

.meeting-room-more-events {
    color: $bg-blue;
    font-weight: 800;
}

.meeting-room-mobile-calendar {
    display: none;
}

.meeting-room-mobile-section-title,
.meeting-room-mobile-agenda-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.meeting-room-mobile-section-title span,
.meeting-room-mobile-agenda-header span {
    display: block;
    color: $bg-blue;
    font-size: 14px;
    font-weight: 900;
    text-transform: capitalize;
}

.meeting-room-mobile-section-title strong,
.meeting-room-mobile-agenda-header strong {
    display: block;
    margin-top: 2px;
    color: #111827;
    font-size: 15px;
    font-weight: 800;
}

.meeting-room-mobile-section-title button,
.meeting-room-mobile-agenda-header button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    min-width: 32px;
    height: 32px;
    border: 0;
    border-radius: 50%;
    background: #ff0d66;
    color: #fff;
}

.meeting-room-mobile-holiday-label {
    display: inline-flex;
    align-items: center;
    align-self: center;
    min-height: 26px;
    padding: 4px 8px;
    border-radius: 14px;
    background: #f1f2f8;
    color: #ff0d66;
    font-size: 12px;
    font-weight: 800;
}

.meeting-room-mobile-month-holiday {
    margin-top: 10px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #f1f2f8;
    color: #ff0d66;
    font-size: 13px;
    font-weight: 900;
    line-height: 1.25;
    text-align: center;
}

.meeting-room-mobile-month-reserve {
    width: 100%;
    min-height: 40px;
    margin-top: 10px;
    border: 0;
    border-radius: 22px;
    background: #ff0d66;
    color: #fff;
    font-size: 13px;
    font-weight: 900;
    text-transform: uppercase;
}

.meeting-room-mobile-timeline {
    display: grid;
    grid-template-columns: 58px minmax(0, 1fr);
    margin-top: 14px;
}

.meeting-room-mobile-time-label {
    height: 52px;
    padding: 8px 8px 0 0;
    color: #4b5563;
    font-size: 12px;
    font-weight: 900;
    text-align: right;
}

.meeting-room-mobile-slot-column {
    position: relative;
}

.meeting-room-mobile-slot {
    position: relative;
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
    width: 100%;
    height: 52px;
    border: 0;
    border-top: 1px solid rgba(71, 76, 230, 0.1);
    border-left: 1px solid rgba(71, 76, 230, 0.1);
    background: #fff;
    color: #ff0d66;
    padding: 7px 8px 0 0;
}

.meeting-room-mobile-slot svg {
    width: 12px;
    height: 12px;
    opacity: 0.45;
}

.meeting-room-mobile-slot.holiday {
    background: #f1f2f8;
    color: #b4b7c9;
    cursor: not-allowed;
}

.meeting-room-mobile-slot.holiday-info {
    background: #fff;
}

.meeting-room-mobile-slot.past {
    background: #fafaff;
    color: #c4c6dc;
    cursor: not-allowed;
}

.meeting-room-mobile-slot.past-time {
    background: #fafaff;
    color: #c4c6dc;
    cursor: not-allowed;
}

.meeting-room-mobile-slot.holiday svg {
    display: none;
}

.meeting-room-mobile-slot.past svg {
    display: none;
}

.meeting-room-mobile-slot.past-time svg {
    display: none;
}

.meeting-room-mobile-events-layer {
    position: absolute;
    inset: 0;
    pointer-events: none;
}

.meeting-room-current-time-indicator--mobile {
    left: 0;
    right: 0;
}

.meeting-room-current-time-indicator--mobile span {
    top: -13px;
    left: 10px;
}

.meeting-room-mobile-event,
.meeting-room-mobile-agenda-card {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 3px;
    border-left: 4px solid $bg-blue;
    border-radius: 8px;
    background: #eef0ff;
    color: #111827;
    line-height: 1.15;
}

.meeting-room-mobile-event {
    position: absolute;
    z-index: 2;
    min-height: 42px;
    padding: 8px 30px 8px 10px;
    overflow: hidden;
    pointer-events: auto;
}

.meeting-room-mobile-event.manageable,
.meeting-room-mobile-agenda-card.manageable {
    cursor: pointer;
}

.meeting-room-mobile-event strong,
.meeting-room-mobile-agenda-card strong {
    color: $bg-blue;
    font-size: 12px;
    font-weight: 900;
}

.meeting-room-mobile-event span,
.meeting-room-mobile-agenda-card span {
    font-size: 13px;
    font-weight: 900;
    word-break: break-word;
}

.meeting-room-mobile-event small,
.meeting-room-mobile-agenda-card small {
    color: #4b5563;
    font-size: 11px;
}

.meeting-room-mobile-event-delete,
.meeting-room-mobile-agenda-card button {
    position: absolute;
    top: 7px;
    right: 7px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border: 0;
    border-radius: 50%;
    background: #ff0d66;
    color: #fff;
    font-size: 10px;
}

.meeting-room-mobile-day-tabs {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 8px;
    margin-bottom: 14px;
}

.meeting-room-mobile-day-tabs button {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 62px;
    border: 1px solid rgba(71, 76, 230, 0.16);
    border-radius: 12px;
    background: #fff;
    color: $bg-blue;
}

.meeting-room-mobile-day-tabs button span {
    font-size: 12px;
    font-weight: 900;
}

.meeting-room-mobile-day-tabs button strong {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    margin-top: 3px;
    border-radius: 50%;
    background: #f4f3ff;
    font-size: 16px;
    font-weight: 900;
}

.meeting-room-mobile-day-tabs button.selected {
    border-color: $bg-blue;
    box-shadow: 0 8px 18px rgba(71, 76, 230, 0.14);
}

.meeting-room-mobile-day-tabs button.selected strong {
    background: $bg-blue;
    color: #fff;
}

.meeting-room-mobile-day-tabs button.today strong {
    background: #ff0d66;
    color: #fff;
}

.meeting-room-mobile-day-tabs button.holiday {
    background: #f1f2f8;
    color: #6b7280;
}

.meeting-room-mobile-day-tabs button.holiday-info {
    background: #fff7fb;
    color: #ff0d66;
}

.meeting-room-mobile-day-tabs button.past:not(.holiday) {
    background: #fafaff;
    color: #9ca3af;
}

.meeting-room-mobile-agenda {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.meeting-room-mobile-agenda-day,
.meeting-room-mobile-month-detail {
    border: 1px solid rgba(71, 76, 230, 0.12);
    border-radius: 14px;
    background: #fff;
    padding: 12px;
}

.meeting-room-mobile-agenda-day.selected {
    border-color: rgba(71, 76, 230, 0.45);
    box-shadow: 0 8px 18px rgba(71, 76, 230, 0.12);
}

.meeting-room-mobile-event-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 10px;
}

.meeting-room-mobile-agenda-card {
    padding: 9px 34px 9px 10px;
}

.meeting-room-mobile-empty {
    margin-top: 10px;
    border-radius: 10px;
    background: #f7f7ff;
    color: #6b7280;
    font-size: 13px;
    font-weight: 800;
    padding: 12px;
    text-align: center;
}

.meeting-room-mobile-month-section {
    background: transparent;
}

.meeting-room-mobile-month-section h4 {
    display: none;
}

.meeting-room-mobile-month-grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    column-gap: 0;
    row-gap: 2px;
    background: transparent;
    padding: 0 4px 8px;
}

.meeting-room-mobile-month-weekday,
.meeting-room-mobile-month-day {
    min-width: 0;
    background: transparent;
    text-align: center;
}

.meeting-room-mobile-month-weekday {
    padding: 0 2px 6px;
    color: $bg-blue;
    font-size: 13px;
    font-weight: 900;
}

.meeting-room-mobile-month-day {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 42px;
    min-height: 42px;
    max-height: 42px;
    border: 0;
    color: #1f2937;
    font-size: 15px;
    font-weight: 900;
    padding: 0;
    overflow: visible;
}

.meeting-room-mobile-month-day span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    aspect-ratio: 1 / 1;
    border-radius: 999px;
    line-height: 1;
}

.meeting-room-mobile-month-day i {
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #ff0d66;
    transform: translateX(-50%);
}

.meeting-room-mobile-month-day b {
    position: absolute;
    right: 4px;
    top: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #ff0d66;
    color: #fff;
    font-size: 9px;
    font-style: normal;
    line-height: 1;
}

.meeting-room-mobile-month-day.muted {
    color: #c4c6dc;
}

.meeting-room-mobile-month-day.holiday {
    background: #f1f2f8;
}

.meeting-room-mobile-month-day.holiday-info {
    background: #fff7fb;
}

.meeting-room-mobile-month-day.past:not(.holiday) {
    color: #9ca3af;
}

.meeting-room-mobile-month-day.selected span {
    width: 38px;
    height: 38px;
    aspect-ratio: 1 / 1;
    background: $bg-blue;
    color: #fff;
    box-shadow: 0 10px 18px rgba(71, 76, 230, 0.22);
}

.meeting-room-mobile-month-day.selected i {
    bottom: 7px;
    background: #fff;
    z-index: 1;
}

.meeting-room-mobile-month-day.today span {
    width: 38px;
    height: 38px;
    aspect-ratio: 1 / 1;
    background: #ff0d66;
    color: #fff;
}

.meeting-room-mobile-month-day.today.selected span {
    background: #ff0d66;
    box-shadow: 0 10px 18px rgba(255, 13, 102, 0.22);
}

.meeting-room-mobile-month-day.today i {
    bottom: 7px;
    background: #fff;
    z-index: 1;
}

.meeting-room-mobile-month-detail {
    margin: 16px -12px -12px;
    border-color: rgba(71, 76, 230, 0.08);
    border-radius: 20px 20px 0 0;
    box-shadow: 0 -8px 24px rgba(17, 24, 39, 0.12);
    max-height: 292px;
    overflow-y: auto;
    padding: 8px 16px 14px;
}

.meeting-room-mobile-month-handle {
    width: 48px;
    height: 4px;
    margin: 0 auto 14px;
    border-radius: 999px;
    background: rgba(71, 76, 230, 0.18);
}

.meeting-room-mobile-month-toggle {
    background: transparent !important;
    color: $bg-blue !important;
    font-size: 15px;
}

:deep(.meeting-room-filter-item .el-date-editor.el-input),
:deep(.meeting-room-filter-item .el-select) {
    width: 100% !important;
    min-height: 44px;
    height: 44px !important;
}

:deep(.meeting-room-filter-item .el-input__wrapper),
:deep(.meeting-room-filter-item .el-select__wrapper) {
    min-height: 42px;
    height: 42px !important;
    padding-left: 18px;
    padding-right: 18px;
    box-sizing: border-box;
}

:deep(.meeting-room-filter-item .el-date-editor .el-input__wrapper) {
    min-height: 42px;
    height: 42px !important;
}

:deep(.meeting-room-filter-item .el-date-editor .el-input__prefix) {
    display: none !important;
}

:deep(.meeting-room-filter-item .el-input__inner) {
    min-width: 0;
    text-overflow: clip;
}

:deep(.meeting-room-filter-item .el-date-editor.el-input),
:deep(.meeting-room-filter-item .el-select) {
    width: 100% !important;
    min-width: 0;
    max-width: 100%;
}

:deep(.meeting-room-filter-item .el-input__wrapper),
:deep(.meeting-room-filter-item .el-select__wrapper) {
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
}

@media (min-width: 768px) and (max-width: 1599px) {
    .card-search {
        height: auto;
        min-height: 142px;
        background-image: none;
    }

    .meeting-room-search-row {
        display: grid;
        grid-template-columns: minmax(300px, 1.15fr) minmax(220px, 0.9fr) minmax(220px, 0.9fr);
        column-gap: 18px;
        row-gap: 14px;
        margin-left: 0;
        margin-right: 0;
        width: 100%;
        max-width: 100%;
    }

    .meeting-room-search-row>div {
        width: 100%;
        max-width: none;
        min-width: 0;
        padding-left: 0;
        padding-right: 0;
    }

    .meeting-room-employee-col {
        grid-column: 1 / 3;
    }

    .meeting-room-actions-col {
        grid-column: 3 / 4;
        justify-content: flex-end;
    }

    .meeting-room-search-row--no-employee {
        grid-template-columns: minmax(300px, 1.05fr) minmax(220px, 0.85fr) minmax(220px, 0.85fr) minmax(330px, 1fr);
    }

    .meeting-room-search-row--no-employee .meeting-room-actions-col {
        grid-column: 4 / 5;
        margin-left: 0;
    }

    .meeting-room-buttons {
        justify-content: flex-end;
        max-width: 100%;
    }

    .meeting-room-button-col {
        width: 150px;
    }

    .btn__search,
    .btn__new {
        height: 40px;
        font-size: 15px;
        padding-left: 14px;
        padding-right: 14px;
    }

    .meeting-room-view-toggle {
        height: 44px;
        border-radius: 22px;
    }

    .meeting-room-view-button {
        min-width: 0;
        padding-left: 8px;
        padding-right: 8px;
        border-radius: 19px;
        font-size: 16px;
        white-space: nowrap;
    }

    .meeting-room-period-title {
        font-size: 24px;
    }

    .meeting-room-calendar-toolbar {
        padding: 14px 16px;
    }

    .meeting-room-icon-button {
        width: 38px;
        min-width: 38px;
        max-width: 38px;
        height: 38px;
        min-height: 38px;
        max-height: 38px;
        flex-basis: 38px;
    }

    .meeting-room-today-button {
        min-width: 78px;
        height: 36px;
        font-size: 13px;
    }

    .meeting-room-time-header,
    .meeting-room-time-body {
        grid-template-columns: 70px repeat(var(--meeting-room-days), minmax(94px, 1fr));
        min-width: 0;
        width: 100%;
    }

    .meeting-room-day-heading {
        min-height: 58px;
        gap: 6px;
    }

    .meeting-room-day-heading strong {
        width: 30px;
        height: 30px;
        font-size: 16px;
    }

    .meeting-room-time-label {
        min-height: 50px;
        padding-right: 10px;
        font-size: 12px;
    }

    .meeting-room-time-cell {
        min-height: 50px;
    }

    .meeting-room-month-calendar {
        grid-template-columns: repeat(7, minmax(96px, 1fr));
    }

    :deep(.meeting-room-filter-item .el-date-editor.el-input),
    :deep(.meeting-room-filter-item .el-select) {
        min-height: 44px;
        height: 44px !important;
    }

    :deep(.meeting-room-filter-item .el-input__wrapper),
    :deep(.meeting-room-filter-item .el-select__wrapper),
    :deep(.meeting-room-filter-item .el-date-editor .el-input__wrapper) {
        min-height: 42px;
        height: 42px !important;
        padding-left: 18px;
        padding-right: 18px;
    }
}

@media (min-width: 768px) and (max-width: 1199px) {
    .meeting-room-search-row {
        grid-template-columns: repeat(2, minmax(280px, 1fr));
    }

    .meeting-room-employee-col,
    .meeting-room-actions-col {
        grid-column: 1 / -1;
    }

    .meeting-room-search-row--no-employee .meeting-room-actions-col {
        grid-column: 1 / -1;
    }

    .meeting-room-actions-col {
        justify-content: flex-start;
    }

    .meeting-room-buttons {
        justify-content: flex-start;
    }

    .meeting-room-header .iconMeeting {
        width: 32px;
        min-width: 32px;
    }

    .meeting-room-header .title-text {
        font-size: 2.7rem;
    }

    .meeting-room-calendar-toolbar {
        align-items: flex-start;
        flex-direction: column;
    }

    .meeting-room-period-actions {
        justify-content: flex-start;
    }
}

@media (min-width: 768px) and (max-width: 991px) {
    .meeting-room-search-row {
        grid-template-columns: 1fr;
    }

    .meeting-room-employee-col,
    .meeting-room-actions-col {
        grid-column: 1 / -1;
    }

    .meeting-room-search-row--no-employee .meeting-room-actions-col {
        grid-column: 1 / -1;
    }

    .meeting-room-buttons {
        width: 100%;
    }

    .meeting-room-button-col {
        width: min(180px, 50%);
    }

    .meeting-room-view-toggle {
        max-width: 420px;
    }
}

@media (max-width: 767px) {
    .meeting-room-header {
        row-gap: 4px;
    }

    .meeting-room-header .iconMeeting {
        width: 24px;
        min-width: 24px;
        margin-top: 6px;
    }

    .meeting-room-header .title-text {
        margin-left: 12px !important;
        font-size: 2.2rem;
        line-height: 1.05;
        text-align: left;
    }

    .card-search {
        height: auto;
        background-image: none;
    }

    .meeting-room-view-toggle {
        grid-template-columns: repeat(3, minmax(0, 1fr));
        align-items: stretch;
    }

    .meeting-room-view-button {
        height: 100%;
        padding: 0;
        text-align: center;
    }

    .meeting-room-search-row {
        row-gap: 6px;
    }

    .meeting-room-search-row>div {
        margin-top: 8px;
    }

    .meeting-room-filter-item label {
        min-height: auto;
    }

    .meeting-room-buttons {
        flex-direction: column;
        align-items: stretch !important;
        margin-top: 0;
        justify-content: center;
    }

    .meeting-room-button-col {
        width: 100%;
        padding: 0;
    }

    .btn__new,
    .btn__search {
        width: 100%;
        min-width: 0;
        margin-left: 0;
        margin-right: 0;
    }

    .meeting-room-calendar-toolbar {
        align-items: center;
        flex-direction: column;
        text-align: center;
    }

    .meeting-room-period-actions {
        justify-content: center;
        width: 100%;
    }

    .meeting-room-period-title {
        font-size: 22px;
    }

    .meeting-room-period-holiday {
        display: flex;
        width: fit-content;
        max-width: 100%;
        margin-left: auto;
        margin-right: auto;
        text-align: center;
    }

    .meeting-room-calendar-shell--mobile-month {
        overflow: visible;
        border-radius: 0;
        background: #fff;
        box-shadow: none;
    }

    .meeting-room-calendar-shell--mobile-month .meeting-room-calendar-toolbar {
        display: grid;
        grid-template-columns: 52px minmax(0, 1fr) 52px;
        grid-template-areas:
            "previous title next"
            ". today .";
        align-items: center;
        gap: 6px 10px;
        padding: 6px 8px 12px;
        border-bottom: 0;
    }

    .meeting-room-calendar-shell--mobile-month .meeting-room-calendar-toolbar>div:first-child,
    .meeting-room-calendar-shell--mobile-month .meeting-room-period-actions {
        display: contents;
    }

    .meeting-room-calendar-shell--mobile-month .meeting-room-period-title {
        grid-area: title;
        font-size: 20px;
        text-align: center;
    }

    .meeting-room-calendar-shell--mobile-month .meeting-room-period-summary {
        display: none;
    }

    .meeting-room-calendar-shell--mobile-month .meeting-room-icon-button.previous {
        grid-area: previous;
        justify-self: start;
    }

    .meeting-room-calendar-shell--mobile-month .meeting-room-icon-button.next {
        grid-area: next;
        justify-self: end;
    }

    .meeting-room-calendar-shell--mobile-month .meeting-room-today-button {
        grid-area: today;
        justify-self: center;
        min-width: 72px;
        height: 32px;
        padding: 0 16px;
    }

    .meeting-room-desktop-calendar {
        display: none;
    }

    .meeting-room-mobile-calendar {
        display: block;
        background: #f7f7ff;
        padding: 12px;
    }

    .meeting-room-calendar-shell--mobile-month .meeting-room-mobile-calendar {
        background: #fff;
        padding: 0 12px 12px;
    }

    .meeting-room-mobile-month-detail .meeting-room-mobile-agenda-header span {
        font-size: 16px;
        text-transform: none;
    }

    .meeting-room-mobile-month-detail .meeting-room-mobile-agenda-header strong {
        display: none;
    }

    .meeting-room-mobile-month-detail .meeting-room-mobile-event-list {
        gap: 8px;
        margin-top: 12px;
        padding-bottom: 2px;
    }

    .meeting-room-mobile-month-detail .meeting-room-mobile-agenda-card {
        border: 1px solid rgba(71, 76, 230, 0.14);
        border-left: 4px solid $bg-blue;
        box-shadow: none;
    }

    .meeting-room-time-header,
    .meeting-room-time-body {
        min-width: 720px;
    }

    .meeting-room-month-calendar {
        grid-template-columns: repeat(7, minmax(105px, 1fr));
    }

    :deep(.meeting-room-dialog .el-dialog) {
        width: calc(100vw - 24px) !important;
        max-width: 750px;
        margin: 12px auto;
    }
}
</style>
